<script lang="ts" setup>
import { getDateRangeFormatted } from '@/services/date'
import { mdiDotsVertical } from '@mdi/js'
import { computed, ref } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { useImage } from '@/shared/composables/image'
import BKSvgIcon from '@/components/AppShared/BKSvgIcon.vue'
import { IEventPreview } from '@/enitites/event/model/types'
import { useRoute, useRouter } from 'vue-router/composables'
import IconLike from '@/shared/icons/IconLike.vue'
import ToggleLike from '@/features/toggle-like/ui/ToggleLike.vue'
import IconTier from '@/features/tickets/assets/IconTier.vue'
import MyTicketsInterface from '@/features/tickets/ui/MyTicketsInterface.vue'
import { ModalWrapper } from '@/shared/ui/modals'

const { getEventImg } = useImage()

const props = defineProps<{
  event: IEventPreview
}>()

const router = useRouter()
const route = useRoute()
const getUserDB = computed(() => store.getters.getUserDB)

const isShowDeleteModal = ref(false)
const isShowTicketsModal = ref(false)
const isMyProfilePage = route.name === 'Profile' && Number(route.params.id) === getUserDB.value.id

const getEventDate = getDateRangeFormatted(props.event.dateStart, props.event.dateEnd)

const handleProvideFeedback = () => {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
    query: { tab: 'feedback' },
  })
}

const handleReviewFeedback = () => {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
    query: { tab: 'feedback' },
  })
}
</script>

<template>
  <div class="event-card-container">
    <div class="event-card">
      <a
        :href="router.resolve({ name: 'Event', params: { id: `${event.id}` } }).href"
        class="event-link"
      >
        <v-img
          :src="getEventImg(event.image, 140)"
          class="event-image"
        />
        <div class="event-info">
          <p class="event-title">
            <span v-if="event.status === 'draft'"> <span class="draft-label">Draft</span> - </span>
            {{ event.title || 'Event name' }}
          </p>
          <p class="event-location">{{ event.locationAddress }}</p>
          <p class="event-date">{{ getEventDate }}</p>
        </div>
      </a>

      <div v-if="event.isFinished">
        <div
          v-if="event.canReviewFeedback"
          class="feedback-wrapper"
        >
          <p class="likes-count">Likes: {{ event.countLikes }} <IconLike color="currentColor" /></p>
          <button @click.stop="handleReviewFeedback">
            Review feedback

            <BKSvgIcon
              class="arrow-icon"
              color="#A38379"
              height="24px"
              name="arrow-with-stem"
              width="24px"
            />
          </button>
        </div>
        <template v-else>
          <div
            v-if="event.canLeaveFeedback || event.canLike"
            class="feedback-wrapper"
          >
            <p>Did you enjoy this event?</p>
            <ToggleLike
              v-if="event.canLike"
              :event-id="event.id"
              :is-liked="event.isLiked"
              color-svg="#A38379"
              @update:is-liked="(newValue) => (event.isLiked = newValue)"
            />
            <button
              v-if="event.canLeaveFeedback"
              @click.stop="handleProvideFeedback"
            >
              Provide feedback

              <BKSvgIcon
                class="arrow-icon"
                color="#A38379"
                height="24px"
                name="arrow-with-stem"
                width="24px"
              />
            </button>
          </div>
          <div v-else>
            <p class="count-likes">Likes <IconLike color="#A38379" /> {{ event.countLikes }}</p>
          </div>
        </template>
      </div>

      <button
        v-if="!event.isFinished && isMyProfilePage && event.ticketCount"
        class="event-tickets"
        @click="isShowTicketsModal = true"
      >
        <IconTier color="#4E3E0E" />
        x {{ event.ticketCount }}
      </button>

      <v-menu
        bottom
        right
        content-class="event-menu"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            class="menu-button"
            v-on="on"
          >
            <v-icon>
              {{ mdiDotsVertical }}
            </v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-if="event.status === 'draft'"
            @click="isShowDeleteModal = true"
          >
            Delete Event
          </v-list-item>
          <v-list-item
            v-if="event.status === 'draft'"
            :to="{ name: 'CreateEventProcessing', params: { id: event.id } }"
            link
          >
            Edit Event
          </v-list-item>
          <v-list-item
            :to="{ name: 'Event', params: { id: event.id } }"
            link
          >
            View Event
          </v-list-item>
          <v-list-item
            v-if="!event.isFinished && isMyProfilePage && event.ticketCount"
            @click="isShowTicketsModal = true"
          >
            View Tickets
          </v-list-item>
        </v-list>
      </v-menu>

      <ModalWrapper
        :is-show="isShowTicketsModal"
        modal-width="904px"
        @update:isShow="isShowTicketsModal = $event"
      >
        <MyTicketsInterface
          v-if="isShowTicketsModal"
          :event-id="event.id"
        />
      </ModalWrapper>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/style/mixins';

.event-card-container {
  container-type: inline-size;
}

.event-card {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 16px;
  color: var(--color-dark-800);
  background-color: var(--color-white);
  border-radius: 10px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 10%);

  @container (max-width: 480px) {
    display: grid;
    grid-template-areas:
      'image feedback menu'
      'info  info     info';
    grid-template-columns: 1fr auto auto;
    grid-template-rows: auto auto;
  }

  p {
    margin: 0;
  }

  .event-link {
    display: contents;
  }

  .event-image {
    grid-area: image;
    flex: 0 0 auto;
    width: 70px;
    height: 70px;
    border-radius: 4px;
  }

  .event-info {
    grid-area: info;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .event-title {
      font-weight: 500;
      font-size: 20px;
      line-height: 1.3;
      color: var(--color-dark-800);
    }

    .event-location,
    .event-date {
      font-size: 14px;
      line-height: 1.3;
      color: var(--color-grey-300);
    }
  }

  .event-tickets {
    grid-area: feedback;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background-color: var(--color-gold-800);
    border-radius: 999px;
    font-size: 14px;
    line-height: 1.3;
    color: #4e3e0e;
  }
}

.count-likes {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #a38379;
}

.draft-label {
  color: #ff2d53;
}

.feedback-wrapper {
  grid-area: feedback;
  flex: 0 1 330px;
  align-self: stretch;
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  color: #a38379;
  font-size: 16px;
  font-weight: 400;
  flex-shrink: 0;

  button {
    display: flex;
    align-items: center;
    gap: 8px;

    .arrow-icon {
      transform: rotate(180deg);
    }
  }

  p {
    margin: 0;
  }

  .likes-count {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.event-menu {
  border-radius: 6px;
  box-shadow: 0 1px 4px rgb(0 0 0 / 20%);
}

.menu-button {
  grid-area: menu;
}
</style>
